import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ClipboardList, RefreshCw, Info, BellRing, CheckCircle2, XCircle, Timer, ShoppingBag, Banknote } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChefHeader from '../shared/ui/ChefHeader';
import { Card, EmptyState, FilterChips, LoadingView, SegmentedPicker, StatCard, Toggle } from '../shared/ui';
import { OrderStatus, formatCurrency, restaurantPayout } from '../shared/models';
import { listenToRestaurantOrders } from '../shared/services/orderService';
import { setRestaurantAcceptingOrders } from '../shared/services/userService';
import NewOrderBanner from '../components/orders/NewOrderBanner';
import { useWakeLock } from '../../../utils/useWakeLock';
import HelpSheet from '../shared/ui/HelpSheet';
import Sheet from '../shared/ui/Sheet';
import OrderCard from '../components/orders/OrderCard';
import { TIME_FILTERS, STATUS_FILTERS, STATUS_CHIP_COLORS, filterOrdersByTime, sortRestaurantOrders } from '../components/orders/orderUtils';

// Mirrors RestaurantDashboardView.swift: business-status toggle, Day/Week/
// Month/Year segmented picker, status filter chips with counts, pending-first
// order list, flashing new-order banner with sound.
export default function OrdersPage() {
  const { restaurant, restaurantId, refreshRestaurant } = useAuth();
  const navigate = useNavigate();

  // Keep the screen awake while on Orders (iOS isIdleTimerDisabled).
  useWakeLock();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [timeFilter, setTimeFilter] = useState('Day');
  const [statusFilter, setStatusFilter] = useState('All');
  const [accepting, setAccepting] = useState(true);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [bannerOrders, setBannerOrders] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  // Requested toggle value awaiting confirmation (iOS Go Offline?/Go Online? alert).
  const [pendingToggle, setPendingToggle] = useState(null);
  const shownOrderIds = useRef(new Set());
  const hasSeededOrders = useRef(false);

  // Keep the toggle in sync with the restaurant doc (iOS reads
  // restaurant.isAcceptingOrders into the view model on load).
  useEffect(() => {
    if (restaurant) setAccepting(restaurant.isAcceptingOrders !== false);
  }, [restaurant]);

  // Realtime orders listener (iOS setupPersistentListener). Each new Pending
  // order is queued into the flashing banner exactly once via shownOrderIds.
  useEffect(() => {
    if (!restaurantId) { setLoading(false); return undefined; }
    setLoading(true);
    setError('');
    const unsub = listenToRestaurantOrders(restaurantId, (list) => {
      setOrders(list);
      setLoading(false);
      setError('');
      // First snapshot = orders that already existed when the page came
      // online — mark them seen so only orders created after this alert.
      if (!hasSeededOrders.current) {
        hasSeededOrders.current = true;
        list.forEach((o) => shownOrderIds.current.add(o.id));
        return;
      }
      const fresh = list.filter(
        (o) => o.status === OrderStatus.PENDING && !shownOrderIds.current.has(o.id)
      );
      fresh.forEach((o) => shownOrderIds.current.add(o.id));
      setBannerOrders((prev) => {
        // Drop banner orders that are no longer pending (handled elsewhere),
        // then append newly-seen pending orders.
        const kept = prev.filter((o) => {
          const cur = list.find((l) => l.id === o.id);
          return cur && cur.status === OrderStatus.PENDING;
        });
        fresh.forEach((o) => { if (!kept.some((k) => k.id === o.id)) kept.push(o); });
        return kept;
      });
    }, (e) => {
      setError(e.message || 'Failed to load orders.');
      setLoading(false);
    });
    return unsub;
  }, [restaurantId, refreshKey]);

  // Ask before flipping online/offline so it can't happen by accident
  // (iOS shows the Go Offline?/Go Online? alert first).
  const requestToggle = (value) => {
    if (value !== accepting && !toggleBusy) setPendingToggle(value);
  };

  const confirmToggle = async () => {
    const value = pendingToggle;
    setPendingToggle(null);
    if (value == null) return;
    await handleToggle(value);
  };

  // iOS updateAcceptingOrdersStatus: optimistic toggle, revert on failure.
  const handleToggle = async (value) => {
    if (!restaurantId || toggleBusy) return;
    setAccepting(value);
    setToggleBusy(true);
    try {
      await setRestaurantAcceptingOrders(restaurantId, value);
      refreshRestaurant?.();
    } catch (e) {
      setAccepting(!value);
      setError('Failed to update order acceptance. Please try again.');
    } finally {
      setToggleBusy(false);
    }
  };

  // Status chip counts are computed from the time-filtered list (iOS does the
  // same — counts reflect the selected Day/Week/Month/Year window).
  const timeFiltered = useMemo(
    () => filterOrdersByTime(orders, timeFilter),
    [orders, timeFilter]
  );

  // Stat cards for the selected timeframe (iOS stats row in
  // RestaurantOrderFilterTabs). Earnings = payout of accepted orders.
  const periodEarnings = useMemo(
    () => timeFiltered
      .filter((o) => o.status !== OrderStatus.PENDING && o.status !== OrderStatus.CANCELLED)
      .reduce((sum, o) => sum + restaurantPayout(o), 0),
    [timeFiltered]
  );

  const chipOptions = useMemo(() => [
    { value: 'All', label: 'All Orders', color: STATUS_CHIP_COLORS.All, count: timeFiltered.length || undefined },
    ...STATUS_FILTERS.map((s) => ({
      value: s,
      color: STATUS_CHIP_COLORS[s],
      label: s === OrderStatus.READY ? 'Ready for Pickup' : s,
      count: timeFiltered.filter((o) => o.status === s).length || undefined,
    })),
  ], [timeFiltered]);

  const visibleOrders = useMemo(() => {
    const filtered = statusFilter === 'All'
      ? timeFiltered
      : timeFiltered.filter((o) => o.status === statusFilter);
    return sortRestaurantOrders(filtered, statusFilter === 'All');
  }, [timeFiltered, statusFilter]);

  const handleBannerOpen = () => {
    const first = bannerOrders[0];
    setBannerOrders([]); // acknowledging stops the sound
    if (first) navigate(`/business/orders/${first.id}`);
  };

  return (
    <div>
      <ChefHeader
        title="Orders"
        right={(
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowHelp(true)}
              aria-label="Orders help"
              className="h-9 px-2 rounded-full flex items-center gap-1 text-blue-500 active:bg-blue-50"
            >
              <Info size={18} />
              <span className="text-sm font-semibold">Orders Info</span>
            </button>
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              aria-label="Refresh orders"
              className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 active:bg-gray-200"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        )}
      />

      <NewOrderBanner
        orders={bannerOrders}
        onOpen={handleBannerOpen}
        onDismiss={() => setBannerOrders([])}
      />

      <div className="px-3 pt-3 space-y-3">
        {/* RestaurantStatusSection — online/offline toggle */}
        <Card className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900">Your Current Business Status</p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${accepting ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className={`text-xs font-medium ${accepting ? 'text-green-600' : 'text-red-500'}`}>
                  {accepting ? 'Online - Accepting Orders' : 'Offline - Not Accepting Orders'}
                </span>
              </div>
            </div>
            <Toggle checked={accepting} onChange={requestToggle} disabled={toggleBusy || !restaurantId} />
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            {accepting
              ? 'Your business is visible to customers and can receive new orders.'
              : 'Your business is hidden from customers and won\'t receive new orders.'}
          </p>
        </Card>

        {/* iOS stats row: Orders / Pending / Completed / Earnings (timeframe) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatCard label="Orders" value={timeFiltered.length} icon={ClipboardList} color="text-red-600" />
          <StatCard label="Pending" value={timeFiltered.filter((o) => o.status === OrderStatus.PENDING).length} icon={BellRing} color="text-orange-600" />
          <StatCard label="Completed" value={timeFiltered.filter((o) => o.status === OrderStatus.COMPLETED).length} icon={CheckCircle2} color="text-green-600" />
          <StatCard label="Earnings" value={formatCurrency(periodEarnings)} icon={Banknote} color="text-blue-600" />
        </div>

        {/* Time filter + status filter tabs */}
        <SegmentedPicker options={TIME_FILTERS} value={timeFilter} onChange={setTimeFilter} />
        <FilterChips options={chipOptions} value={statusFilter} onChange={setStatusFilter} fill />

        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl p-3">
            <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-medium text-red-600">{error}</p>
              <button
                onClick={() => setRefreshKey((k) => k + 1)}
                className="text-xs font-bold text-red-600 underline mt-1"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {loading && orders.length === 0 ? (
          <LoadingView label="Loading orders…" />
        ) : visibleOrders.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="No Orders"
            subtitle="New orders will appear here."
          />
        ) : (
          <div className="space-y-2.5 pb-4">
            {visibleOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onOpen={() => navigate(`/business/orders/${order.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* iOS Go Offline?/Go Online? confirmation alert */}
      <Sheet
        open={pendingToggle !== null}
        onClose={() => setPendingToggle(null)}
        title={pendingToggle ? 'Go Online?' : 'Go Offline?'}
        maxHeight="45vh"
      >
        <p className="px-4 pb-3 text-sm text-gray-500">
          {pendingToggle
            ? 'Your business will be visible to customers and can start receiving orders.'
            : "Your business will be hidden from customers and won't receive new orders."}
        </p>
        <div className="px-4 pb-4 flex gap-2">
          <button
            onClick={() => setPendingToggle(null)}
            className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 text-[15px] font-semibold active:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={confirmToggle}
            className={`flex-1 py-3 rounded-xl text-white text-[15px] font-semibold ${
              pendingToggle ? 'bg-green-600 active:bg-green-700' : 'bg-red-600 active:bg-red-700'
            }`}
          >
            {pendingToggle ? 'Go Online' : 'Go Offline'}
          </button>
        </div>
      </Sheet>

      {/* iOS "Handling Orders" HowToGuideView */}
      <HelpSheet
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title="Handling Orders"
        subtitle="How a customer order moves through your store."
        steps={[
          { Icon: BellRing, title: 'New order arrives', detail: 'New orders show an orange badge and a banner up top. Tap the order to open it.' },
          { Icon: CheckCircle2, title: 'Accept it fast', detail: 'Open the order and tap Accept/Confirm quickly — customers are watching their order status.' },
          { Icon: XCircle, title: 'Reject if needed', detail: "Can't fulfil it? Reject the order with a reason so the customer is notified right away." },
          { Icon: Timer, title: 'Preparing', detail: 'Mark the order Preparing while you get it ready.' },
          { Icon: ShoppingBag, title: 'Ready', detail: 'Mark it Ready when done — the driver (for delivery) or the customer (for pickup) takes it from here.' },
          { Icon: Banknote, title: 'Your payout', detail: 'You keep 90% of the item total on every completed order. Admin settles payouts from the Payouts tab.' },
        ]}
      />
    </div>
  );
}
